import { Button } from "@msuite/ui/button";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { type RowActionsProps, RowActionsWrapper } from "@msuite/ui/table";
import { toast } from "@msuite/ui/toast";
import { HintTooltip } from "@msuite/ui/tooltip";
import { responseHandler } from "@msuite/ui/utils";
import { Fragment } from "react";
import { client } from "@/hono";
import type { UserRoleRow } from "./table-config";

/**
 * Activate or deactivate one role for the user — the same membership edge the role page
 * writes, seen from the person's side. Deactivating confirms first: it is the same act as
 * removing the person from the role, and it revokes permissions the moment it lands.
 */
export function UserRoleRowActions({
	row,
	userId,
	withActions,
}: RowActionsProps<UserRoleRow> & { userId: string; withActions: boolean }) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const asyncModal = useAsyncModal();

	/** Functions */
	async function handleOnToggle() {
		const { id: roleId, isActive } = row.original;
		if (isActive) {
			const isConfirmed = await asyncModal.confirm();
			if (!isConfirmed) return;
		}
		try {
			const param = { roleId, userId };
			const response = isActive
				? await client.roles[":roleId"].members[":userId"].$delete({ param })
				: await client.roles[":roleId"].members[":userId"].$put({ param });
			// One message per outcome, raised here: the handler's own toast is suppressed so a
			// failure cannot end up reported twice, or — when the request never reaches the
			// server — not at all.
			await responseHandler(response, false);
			toast.success(isActive ? t("roles.member_removed") : t("roles.member_added"));
			await qc.invalidateQueries({
				predicate: invalidate(["user-permissions-form", "roles", "citadel"]),
			});
		} catch (error) {
			toast.error(error instanceof Error ? error.message : t("general.something_went_wrong"));
		}
	}

	/** Guard */
	// The member system role is standing membership — nothing to activate, nothing to remove.
	// The hint takes the menu's place, in the same 28px box, so the column's icons share one
	// vertical axis whatever the row offers — and it renders for every reader, including the
	// ones without role-write rights, who cannot discover the reason by trying the toggle.
	if (row.original.isLocked) {
		return (
			<RowActionsWrapper>
				<div className="size-7 flex-center">
					<HintTooltip label={t("roles.member_always_active")} />
				</div>
			</RowActionsWrapper>
		);
	}

	// Everyone else sees a plain row: the toggles are role writes, and the server refuses them
	// for callers who are neither owner nor admin.
	if (!withActions) return <Fragment />;

	/** Render */
	return (
		<Fragment>
			<RowActionsWrapper>
				<Menu
					items={() => [
						row.original.isActive
							? {
									label: t("roles.deactivate"),
									icon: icon("UserRoundMinus"),
									onClick: handleOnToggle,
									isDanger: true,
									cyId: `user-role-deactivate-item-${row.original.id}`,
								}
							: {
									label: t("roles.activate"),
									icon: icon("UserRoundPlus"),
									onClick: handleOnToggle,
									cyId: `user-role-activate-item-${row.original.id}`,
								},
					]}
				>
					<Button
						size="xxs"
						variant="ghost"
						icon={<Icon name="EllipsisVertical" />}
						data-cy={`user-role-row-menu-${row.original.id}`}
					/>
				</Menu>
			</RowActionsWrapper>
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					title={t("roles.deactivate")}
					message={t("roles.deactivate_confirmation", { role: row.original.name })}
					confirmLabel={t("roles.deactivate")}
				/>
			)}
		</Fragment>
	);
}
